import {
  DsaAvailableProducts,
  DsaExistingProducts,
  DsaProducts,
  HsmMobilityType,
} from '../constants/common.constants';
import { capitalizeWord } from '../lib/utils';

/*
[DPD-2298] DSA Model
This model is used to display the address details in the address 360 page.
The attributes and methods have been copied over from D2C One App to ensure both apps are in sync.
*/

export interface IDsa {
  '@baseType'?: string;
  '@type'?: string;
  address: IDsaAddress;
  addressSellStatus: string;
  coId: string;
  customer: IDsaCustomer;
  d2cSellDecision?: any[];
  doNotKnock: boolean;
  firstNations: boolean;
  fmsAddressId: string;
  fsa: string;
  href: string;
  id: string;
  lastKnockDate: string;
  lastKnockStatus: string;
  lpdsId: string;
  mduType: string;
  networkTech: IDsaNetworkTech;
  partnerStatus: string;
  premiseTypeSnet: string;
  prizmLifestageSegmentation: string;
  prizmSegmentation: string;
  prizmSocialSegmentation: string;
  provisioningSystem?: string;
}

export interface IDsaAddress {
  city: string;
  dirPrefix?: string;
  dirSuffix?: string;
  fullAddress: string;
  postalCode: string;
  prefix?: string;
  province: string;
  streetName: string;
  streetNumber: string;
  streetNumberSuffix?: string;
  streetType: string;
  suffix?: string;
  unit?: string;
}

export const productsSHSAlias = {
  adt: 'ADT',
  telusSecurity: 'security',
  acme: 'ACME',
  ambuschToAcme: 'ACME',
  paladinToAcme: 'ACME',
  curtisElite: 'Curtis Elite',
  graydon: 'Graydon',
  price: 'Price',
  vivint: 'Vivint',
  security: 'Security',
};

export interface IDsaCustomer {
  lifetimeCurrentValue?: string;
  lifetimePotentialValue?: string;
  multiculturalProfile?: string;
  products: IDsaProduct[];
  rguPotentialIncremental: number;
  telusRewards: boolean;
}

export interface IDsaProduct {
  '@baseType'?: string;
  '@type'?: string;
  contractEndDate?: Date;
  eligible?: boolean;
  koodoSubscriberCount?: number;
  product: string;
  provisioned: boolean;
  publicSubscriberCount?: number;
  telusAccountType?: string;
  telusSubscriberCount?: number;
  wirelessHomePhoneEligible?: boolean;
  smartWear?: boolean;
  telusSecurity?: boolean;
  acme?: boolean;
  adt?: boolean;
  ambuschToAcme?: boolean;
  curtisElite?: boolean;
  graydon?: boolean;
  price?: boolean;
  paladinToAcme?: boolean;
  vivint?: boolean;
  wirelessHsiaEligible?: boolean;
  dialUpEligible?: boolean;
  dialUpProvisioned?: boolean;
}

export interface IDsaNetworkTech {
  fibreEligible: boolean;
  fibreProvisioned: boolean;
  copperProvisioned: boolean;
  migrationEligible: boolean;
  currentSpeedMbit?: number;
  maxSpeedMbit: number;
  diyInstallEligible?: string;
  dropConstructed: IDsaDropConstructed;
  networkProviders: IDsaNetworkProvider[];
  whsia?: IWhsiaTech;
}

export interface IDsaNetworkProvider {
  currentSpeedMbit?: number;
  maxSpeedMbit: number;
  provider: string;
}

export interface IWhsiaTech {
  currentSpeedMbit?: number;
  available?: IWhsiaType[];
}

export interface IWhsiaType {
  type: string;
  maxSpeedMbit: number;
}

export interface IDsaDropConstructed {
  status?: string;
  constructedDate?: Date;
  reportingRegion: string;
  obdEligibleInd: boolean;
}

const booleanYesNo = {
  yes: 'Yes',
  no: 'No',
};
export class Dsa implements IDsa {
  private state: IDsa;

  constructor(hsm: IDsa) {
    this.state = hsm;
  }

  get address(): IDsaAddress {
    return this.state?.address;
  }

  get addressSellStatus(): string {
    return this.state?.addressSellStatus;
  }

  get coId(): string {
    return this.state?.coId + this.state?.address.province; // DSA has 'NVCR' so add province to make 'NVCRBC';
  }

  get customer(): IDsaCustomer {
    return this.state?.customer;
  }

  get sellDecisionList(): any {
    return this.state?.d2cSellDecision;
  }

  get doNotKnock(): boolean {
    return this.state?.doNotKnock;
  }

  get firstNations(): boolean {
    return this.state?.firstNations;
  }

  get fmsAddressId(): string {
    return this.state?.fmsAddressId;
  }

  get fsa(): string {
    return this.state?.fsa;
  }

  get href(): string {
    return this.state?.href;
  }

  get id(): string {
    return this.state?.id;
  }

  get lastKnockDate(): string {
    return this.state?.lastKnockDate;
  }

  get lastKnockStatus(): string {
    return this.state?.lastKnockStatus;
  }

  get lpdsId(): string {
    return this.state?.lpdsId;
  }

  get mduType(): string {
    return this.state?.mduType;
  }

  get networkTech(): IDsaNetworkTech {
    return this.state?.networkTech;
  }

  get partnerStatus(): string {
    return this.state?.partnerStatus;
  }

  get premiseTypeSnet(): string {
    return this.state?.premiseTypeSnet;
  }

  get prizmLifestageSegmentation(): string {
    return this.state?.prizmLifestageSegmentation;
  }

  get prizmSegmentation(): string {
    return this.state?.prizmSegmentation;
  }

  get prizmSocialSegmentation(): string {
    return this.state?.prizmSocialSegmentation;
  }

  get provisioningSystem(): string {
    return this.state?.provisioningSystem ?? '';
  }

  get isUnderAbOrBcProvince(): boolean {
    return this.address.province === 'AB' || this.address.province === 'BC';
  }

  get isSmartHubProvisioned(): boolean {
    const shub = this.getProduct(DsaProducts.shub);
    if (
      (shub?.provisioned || false) &&
      (this.networkTech?.whsia?.currentSpeedMbit ?? 0 > 0)
    ) {
      return true;
    }
    return false;
  }

  //DPD-801: an address is whsia eligible if it has networkTech.whsia.available types
  //smartHub is eligible if it has whsia eligibility && min copper speed 11.5
  get isSmartHubAvailable(): boolean {
    const shub = this.getProduct(DsaProducts.shub);
    if (
      (shub?.eligible || false) &&
      (this.networkTech?.whsia?.available?.length || 0 > 0)
    ) {
      return true;
    }
    return false;
  }

  get currentTech(): string {
    /*
      If network provider is telus then address can have copper or fibre or whsia as current tech
      Else address can be Koodo based or None
     */
    const provider = this.getCurrentNetworkProvider(); // Telus or Bell or Koodo or None
    if (provider == 'Telus') {
      const fiber = this.networkTech?.fibreProvisioned;
      const copper = this.networkTech?.copperProvisioned;
      const whsia = this.isSmartHubProvisioned;

      if (fiber) {
        return 'Fibre';
      } else if (!fiber && copper && whsia) {
        return 'Copper';
      } else if (copper && !whsia) {
        return 'Copper';
      } else if (!copper && whsia) {
        return DsaExistingProducts.smarthub;
      } else {
        return 'None';
      }
    } else if (provider == 'Bell') {
      return 'Fibre'; //Bell fibre
    }
    return !this.networkTech.currentSpeedMbit ? 'None' : provider;
  }

  get availableTech(): string {
    let availableTech = this.getAvailableNetworkProvider();

    if (availableTech == 'Telus' || availableTech == 'Bell') {
      // [DPD-1952] available tech from DSA
      availableTech = this.networkTech.fibreEligible
        ? 'Fibre'
        : this.networkTech.maxSpeedMbit >= 15
        ? 'Copper'
        : this.isSmartHubAvailable
        ? DsaExistingProducts.smarthub
        : 'None';
      if (availableTech == 'None') {
        return availableTech;
      }

      let maxSpeed = Math.max(
        ...this.networkTech.networkProviders.map(
          (network) => network.maxSpeedMbit
        )
      );
      if (availableTech == DsaExistingProducts.smarthub) {
        maxSpeed = Math.max(
          ...(this.networkTech.whsia?.available || []).map(
            (type) => type.maxSpeedMbit
          )
        );
      }
      if (maxSpeed) {
        const availableMaxSpeed =
          maxSpeed < 1000 ? maxSpeed : `${maxSpeed / 1000}Gb`;
        availableTech = `${availableTech} (max ${availableMaxSpeed})`;
      }
    } else {
      // [DPD-2074] Koodo
      if (availableTech !== 'None') {
        const maxSpeed = Math.max(
          ...this.networkTech.networkProviders.map(
            (network) => network.maxSpeedMbit
          )
        );
        const availableMaxSpeed =
          maxSpeed < 1000 ? maxSpeed : `${maxSpeed / 1000}Gb`;
        availableTech = `${availableTech} (max ${availableMaxSpeed})`;
      }
    }
    return `${availableTech}`;
  }

  get productsSHSString(): any {
    const hsmSecurity = this.getProduct(DsaProducts.security);
    let shsProductsString = '';

    if (hsmSecurity) {
      if (hsmSecurity?.adt) {
        shsProductsString = productsSHSAlias.adt;
      }

      if (hsmSecurity?.curtisElite) {
        shsProductsString = productsSHSAlias.curtisElite;
      }

      if (hsmSecurity?.graydon) {
        shsProductsString = productsSHSAlias.graydon;
      }

      if (hsmSecurity?.price) {
        shsProductsString = productsSHSAlias.price;
      }

      if (hsmSecurity?.vivint) {
        shsProductsString = productsSHSAlias.vivint;
      }

      if (
        hsmSecurity?.acme ||
        hsmSecurity?.ambuschToAcme ||
        hsmSecurity?.paladinToAcme
      ) {
        shsProductsString = productsSHSAlias.acme;
      }

      if (hsmSecurity?.telusSecurity) {
        shsProductsString = productsSHSAlias.telusSecurity;
      }

      return shsProductsString ? shsProductsString : '';
    } else {
      return shsProductsString;
    }
  }

  get internetString(): string {
    //possible outpus: No, Internet 100, Koodo 150, whsia 75
    let availableTech =
      this.getCurrentNetworkProvider() === 'Koodo'
        ? this.getCurrentNetworkProvider()
        : capitalizeWord(DsaProducts.internet);
    let maxSpeed = this.networkTech?.currentSpeedMbit ?? 0;
    const internetSpeed = this.networkTech?.currentSpeedMbit;
    if (!internetSpeed || internetSpeed <= 0) {
      if (this.isSmartHubProvisioned) {
        availableTech = capitalizeWord(DsaProducts.shub);
        maxSpeed = this.networkTech?.whsia?.currentSpeedMbit ?? 0;
      } else {
        return booleanYesNo.no;
      }
    }
    const availableMaxSpeed =
      maxSpeed < 1000 ? maxSpeed : `${maxSpeed / 1000}Gb`;
    return `${availableTech} ${availableMaxSpeed}`;
  }

  get existingProducts(): string {
    const hsmSHSText = this.productsSHSString;
    const hsmMobility = this.getProduct(DsaProducts.mobility);
    const hsmHSIA = this.internetString;
    const telusCount = hsmMobility?.telusSubscriberCount ?? 0;
    const koodoCount = hsmMobility?.koodoSubscriberCount ?? 0;
    const publicCount = hsmMobility?.publicSubscriberCount ?? 0;
    const text: string[] = [];

    if (hsmHSIA !== booleanYesNo.no) {
      text.push(hsmHSIA);
    }

    if (telusCount > 0) {
      text.push(
        telusCount > 1
          ? `${HsmMobilityType.telus} (${telusCount})`
          : HsmMobilityType.telus
      );
    }

    if (koodoCount > 0) {
      text.push(
        koodoCount > 1
          ? `${HsmMobilityType.koodo} (${koodoCount})`
          : HsmMobilityType.koodo
      );
    }

    if (publicCount > 0) {
      text.push(
        publicCount > 1
          ? `${HsmMobilityType.public} (${publicCount})`
          : HsmMobilityType.public
      );
    }

    //Add existing products
    const existingProducts = this.getExistingProducts();
    if (!!existingProducts) {
      text.push(existingProducts);
    }

    if (!!hsmSHSText) {
      text.push(hsmSHSText);
    }

    return text.join(', ');
  }

  get availableProducts(): string {
    let qualificationString = this.getAvailableProducts();
    // insert HSM Internet
    if (this.availableTech === 'None') {
      // DPD-2079
      qualificationString = `${qualificationString}`;
    } else {
      qualificationString = `${this.availableTech}, ${qualificationString}`;
    }

    return qualificationString;
  }

  //get current network provider for telus/bell/koodo/none
  //if address has whsia provisioned then treat as telus
  getCurrentNetworkProvider(): string {
    //telus serving area
    if (this.isUnderAbOrBcProvince) {
      return 'Telus';
    }
    //telus serving area but not AB/BC like QC
    if (
      this.networkTech?.networkProviders
        ?.map((network) => network.provider.toLowerCase())
        .includes('telus')
    ) {
      return 'Telus';
    }
    //no internet service provider and no whsia either
    if (
      this.networkTech?.networkProviders?.length === 0 &&
      this.isSmartHubProvisioned
    ) {
      return 'None';
    } else if (
      this.networkTech?.networkProviders?.length === 0 &&
      this.isSmartHubProvisioned
    ) {
      return 'Telus';
    }
    const currentProvider =
      this.networkTech?.networkProviders?.find(
        (network) => network.currentSpeedMbit
      )?.provider ?? 'None';
    if (currentProvider != 'Bell' && currentProvider != 'None') {
      return 'Koodo';
    }
    return currentProvider;
  }

  //[Vulcan] get available network provider for telus/koodo/none
  //if bell is available then display treat as telus and has higher priority than koodo
  //if whsia is eligible then treat as telus
  getAvailableNetworkProvider(): string {
    //telus serving area
    if (this.isUnderAbOrBcProvince) {
      return 'Telus';
    }
    //telus serving area
    if (
      this.networkTech?.networkProviders
        ?.map((network) => network.provider.toLowerCase())
        .includes('telus')
    ) {
      return 'Telus';
    }
    //bell serving area
    if (
      this.networkTech?.networkProviders
        ?.map((network) => network.provider.toLowerCase())
        .includes('bell')
    ) {
      return 'Telus';
    }
    //not telus nor koodo nor whsia eligible
    if (
      this.networkTech?.networkProviders?.length === 0 &&
      !this.isSmartHubAvailable
    ) {
      return 'None';
    } else if (
      this.networkTech?.networkProviders?.length === 0 &&
      this.isSmartHubAvailable
    ) {
      return 'Telus';
    }

    return 'Koodo';
  }

  getProduct(product: DsaProducts): IDsaProduct | null {
    return (
      this.customer?.products.find((prod) => prod.product === product) || null
    );
  }

  getExistingProducts(): string | null {
    return (
      this.customer?.products
        .filter(
          (prod) =>
            Object.keys(DsaExistingProducts).includes(
              prod.product as DsaExistingProducts
            ) && prod.provisioned === true
        )
        .map(
          (prod) =>
            DsaExistingProducts[
              prod.product as keyof typeof DsaExistingProducts
            ]
        )
        .join(', ') || null
    );
  }

  getAvailableProducts(): string | null {
    return (
      this.customer?.products
        .filter(
          (prod) =>
            Object.keys(DsaAvailableProducts).includes(
              prod.product as DsaAvailableProducts
            ) && prod.eligible === true
        )
        .map(
          (prod) =>
            DsaAvailableProducts[
              prod.product as keyof typeof DsaAvailableProducts
            ]
        )
        .join(', ') || ''
    );
  }
}
